import axios from 'axios';

// Finanza: report mensile (fotografie su richiesta), costi manuali e tariffe delle commissioni
const state = {
	report: null,
	costs: [],
	settings: null,
	keys: { channels: [], payments: [] },
};

const getters = {
	getReport: state => state.report,
	getCosts: state => state.costs,
	getSettings: state => state.settings,
	getKeys: state => state.keys,
};

// "Aggiorna" di ciascun report
const REFRESH_PATHS = { finance: 'finance/refresh', customers: 'finance/customers/refresh', inventory: 'finance/inventory/refresh', foreign: 'finance/foreign/refresh' };

const auth = context => ({ headers: { Authorization: `Bearer ${context.rootGetters['profile/getToken']}` } });
const url = (context, path) => `${context.rootGetters['settings/getUrl']}${path}`;

const actions = {
	// from/to 'YYYY-MM': i mesi mai aperti vengono calcolati e salvati dal server alla prima richiesta
	fetchReport: async (context, { from, to, platform, compare } = {}) => {
		const res = await axios.get(url(context, 'finance/report'), { ...auth(context), params: { from, to, platform: platform || undefined, compare: compare || undefined } });
		context.commit('setReport', res.data.data);
		return res.data.data;
	},
	// scheda Previsioni: mese in corso e prossimi 6 mesi per canale, con il margine
	fetchForecastMonths: async (context, { platform } = {}) => (await axios.get(url(context, 'forecast/months'), { ...auth(context), params: { platform: platform || undefined } })).data.data,
	recompute: async (context, month) => (await axios.post(url(context, `finance/recompute/${month}`), {}, auth(context))).data.data,

	fetchCosts: async (context, settings) => {
		const { sortBy, sortDesc, page, itemsPerPage } = settings.options;
		const filters = settings.filters || {};
		const res = await axios.get(url(context, 'finance/costs'), {
			...auth(context),
			params: {
				page,
				perPage: itemsPerPage,
				sortBy: sortBy[0],
				sortDesc: sortDesc[0] ? 'true' : 'false',
				search: settings.search || undefined,
				category: filters.category || undefined,
				from: filters.from || undefined,
				to: filters.to || undefined,
			},
		});
		context.commit('setCosts', res.data.data);
		return { total: res.data.total };
	},
	fetchCost: async (context, id) => (await axios.get(url(context, `finance/costs/${id}`), auth(context))).data.data,
	saveCost: async (context, { id, data }) => {
		const res = id ? await axios.put(url(context, `finance/costs/${id}`), data, auth(context)) : await axios.post(url(context, 'finance/costs'), data, auth(context));
		return res.data.data;
	},
	deleteCost: async (context, id) => (await axios.delete(url(context, `finance/costs/${id}`), auth(context))).data.data,

	fetchSettings: async context => {
		const res = await axios.get(url(context, 'finance/settings'), auth(context));
		context.commit('setSettings', res.data.data.settings);
		context.commit('setKeys', res.data.data.keys);
		return res.data.data;
	},
	saveSettings: async (context, data) => {
		const res = await axios.put(url(context, 'finance/settings'), data, auth(context));
		context.commit('setSettings', res.data.data);
		return res.data.data;
	},
	// commissioni del mese per fonte: API, totale manuale, tariffa
	fetchFees: async (context, month) => (await axios.get(url(context, 'finance/fees'), { ...auth(context), params: { month } })).data.data,
	saveManualFee: async (context, data) => (await axios.put(url(context, 'finance/fees/manual'), data, auth(context))).data.data,
	syncFees: async (context, month) => (await axios.post(url(context, `finance/fees/sync/${month}`), {}, auth(context))).data.data,
	// "Aggiorna": ricalcolo dei mesi del periodo in coda (kind: 'finance' | 'customers') e suo avanzamento
	startRefresh: async (context, { kind, from, to }) => (await axios.post(url(context, REFRESH_PATHS[kind] || REFRESH_PATHS.finance), { from, to }, auth(context))).data.data,
	refreshStatus: async (context, kind) => (await axios.get(url(context, REFRESH_PATHS[kind] || REFRESH_PATHS.finance), auth(context))).data.data,

	// report scorte
	fetchInventoryReport: async context => (await axios.get(url(context, 'finance/inventory/report'), auth(context))).data.data,
	fetchInventoryList: async (context, params) => {
		const res = await axios.get(url(context, 'finance/inventory/list'), { ...auth(context), params });
		return { items: res.data.data, total: res.data.total };
	},
	setInventoryOverride: async (context, { key, qty }) => (await axios.put(url(context, 'finance/inventory/override'), { key, qty }, auth(context))).data.data,
	clearInventoryOverrides: async (context, supplier) => (await axios.post(url(context, 'finance/inventory/override/clear'), { supplier }, auth(context))).data.data,
	fetchInventoryPrint: async (context, supplier) => (await axios.get(url(context, 'finance/inventory/print'), { ...auth(context), params: { supplier } })).data.data,
	fetchInventorySettings: async context => (await axios.get(url(context, 'finance/inventory/settings'), auth(context))).data.data,
	saveInventorySettings: async (context, data) => (await axios.put(url(context, 'finance/inventory/settings'), data, auth(context))).data.data,

	// report IVA estera e OSS: trimestre (quarter 1-4) o anno intero
	fetchForeignReport: async (context, { year, quarter } = {}) =>
		(await axios.get(url(context, 'finance/foreign/report'), { ...auth(context), params: { year, quarter: quarter || undefined } })).data.data,
	fetchForeignSettings: async context => (await axios.get(url(context, 'finance/foreign/settings'), auth(context))).data.data,
	saveForeignSettings: async (context, data) => (await axios.put(url(context, 'finance/foreign/settings'), data, auth(context))).data.data,
	foreignVatCheck: async (context, { year, quarter }) => (await axios.post(url(context, 'finance/foreign/vat-check'), { year, quarter: quarter || null }, auth(context))).data.data,

	// report clienti
	fetchCustomersReport: async (context, { from, to, platform, compare } = {}) =>
		(await axios.get(url(context, 'finance/customers/report'), { ...auth(context), params: { from, to, platform: platform || undefined, compare: compare || undefined } })).data.data,
	fetchCohorts: async (context, { from, to, platform } = {}) =>
		(await axios.get(url(context, 'finance/customers/cohorts'), { ...auth(context), params: { from, to, platform: platform || undefined } })).data.data,
	fetchCustomersList: async (context, settings) => {
		const { sortBy, sortDesc, page, itemsPerPage } = settings.options;
		const f = settings.filters || {};
		const res = await axios.get(url(context, 'finance/customers/list'), {
			...auth(context),
			params: {
				page,
				perPage: itemsPerPage,
				sortBy: sortBy[0],
				sortDesc: sortDesc[0] ? 'true' : 'false',
				search: settings.search || undefined,
				segment: f.segment || undefined,
				platform: f.platform || undefined,
				country: f.country || undefined,
				oneTime: f.oneTime ? 'true' : undefined,
			},
		});
		return { items: res.data.data, total: res.data.total };
	},
	fetchSegmentCustomers: async (context, { segment, platform }) =>
		(await axios.get(url(context, 'finance/customers/segment-customers'), { ...auth(context), params: { segment, platform: platform || undefined } })).data.data,
	fetchSegments: async context => (await axios.get(url(context, 'finance/customers/segments'), auth(context))).data.data,
	customersRecompute: async (context, month) => (await axios.post(url(context, `finance/customers/recompute/${month}`), {}, auth(context))).data.data,
	destroyData: context => {
		context.commit('setReport', null);
		context.commit('setCosts', []);
	},
};

const mutations = {
	setReport: (state, value) => {
		state.report = value;
	},
	setCosts: (state, value) => {
		state.costs = value;
	},
	setSettings: (state, value) => {
		state.settings = value;
	},
	setKeys: (state, value) => {
		state.keys = value || { channels: [], payments: [] };
	},
};

export default {
	namespaced: true,
	state,
	mutations,
	getters,
	actions,
};
